import { Cubit } from '@breakoutlearning/firebase-repository/cubits/core'
import type { BreakoutUser } from '@breakoutlearning/firebase-repository/models/BreakoutUser'
import { action, computed, makeObservable, observable } from 'mobx'
import { api } from 'config/api'
import {
  SCALES,
  buildScoreMappingDefaults,
  defaultMappingForScale,
  isScoreMappingDescending,
  type ScaleDef,
  type ScaleId,
  type ScoreMappingDefaultEntry,
} from './customRubricsConfig'

type Mappings = Record<ScaleId, Record<string, number>>

/** loading → the initial fetch is in flight; error → it failed (mappings are
 * still seeded from the global defaults so the assignment dialog can keep
 * working, but saving is disabled); ready → seeded from the saved defaults. */
export type ScoreMappingDefaultsStatus = 'loading' | 'error' | 'ready'

export type ScoreMappingDefaultsCubitConfig = {
  /** Scales this cubit manages. The Settings page passes all SCALES; the
   * per-assignment dialog passes only the scales present in the assignment. */
  scales?: readonly ScaleDef[]
  /** Per-scale seed override — the assignment's existing mapping in the dialog.
   * When it returns undefined the saved default, then the global default, is
   * used. */
  seedOverride?: (scale: ScaleDef) => Record<string, number> | undefined
  /** When true (Settings page) `save()` persists `assignFinalGrade` and it
   * factors into `dirty`. When false (dialog) the toggle is omitted so the
   * server's merge preserves whatever was already saved. */
  managesAssignFinalGrade?: boolean
}

/**
 * Owns the load / seed / edit / validate / save lifecycle for a user's saved
 * score-mapping defaults, shared by the Settings subpage (all scales, plus the
 * `assignFinalGrade` toggle) and the per-assignment "Save as Defaults" control
 * (only the assignment's scales, seeded from its existing mapping). Views are
 * thin observers over `status` / `mappings` / `valid` / `dirty` / `canSave`.
 */
export class ScoreMappingDefaultsCubit extends Cubit {
  private readonly user: BreakoutUser
  private readonly scales: readonly ScaleDef[]
  private readonly seedOverride?: (
    scale: ScaleDef
  ) => Record<string, number> | undefined
  private readonly managesAssignFinalGrade: boolean

  @observable status: ScoreMappingDefaultsStatus = 'loading'
  @observable saving = false
  @observable.ref mappings: Mappings = {} as Mappings
  @observable assignFinalGrade = false

  // Baselines captured on load and after a successful save: the raw saved
  // defaults (to carry over scales this cubit doesn't manage) plus the seed the
  // current mappings are compared against for `dirty`. Observable so the
  // `dirty` / `differsFromSaved` computeds invalidate when a save advances them
  // (otherwise the cached value keeps the button enabled after saving).
  @observable.ref private savedDefaults: ScoreMappingDefaultEntry[] = []
  @observable.ref private seed: Mappings = {} as Mappings
  @observable private savedAssignFinalGrade = false

  constructor(
    user: BreakoutUser,
    config: ScoreMappingDefaultsCubitConfig = {}
  ) {
    super()
    this.user = user
    this.scales = config.scales ?? SCALES
    this.seedOverride = config.seedOverride
    this.managesAssignFinalGrade = config.managesAssignFinalGrade ?? false
    makeObservable(this)
  }

  async initialize() {
    try {
      const doc = await this.user.fetchScoreMappingDefaults()
      this.applyLoaded('ready', doc.defaults, doc.assignFinalGrade)
    } catch {
      // Seed from the global defaults so the dialog still works, but stay in
      // `error` so saving is disabled — otherwise a transient failure could
      // overwrite the user's (unloaded) saved defaults.
      this.applyLoaded('error', [], false)
    }
  }

  private seedMappings(defaults: ScoreMappingDefaultEntry[]): Mappings {
    const seed = {} as Mappings
    for (const scale of this.scales) {
      seed[scale.id] =
        this.seedOverride?.(scale) ??
        defaultMappingForScale(scale, defaults) ??
        scale.defaultMapping
    }
    return seed
  }

  @action
  private applyLoaded(
    status: ScoreMappingDefaultsStatus,
    defaults: ScoreMappingDefaultEntry[],
    assignFinalGrade: boolean
  ) {
    if (this.isDisposed) return
    this.savedDefaults = defaults
    this.savedAssignFinalGrade = assignFinalGrade
    this.assignFinalGrade = assignFinalGrade
    const seed = this.seedMappings(defaults)
    this.seed = seed
    this.mappings = seed
    this.status = status
  }

  @computed
  get valid(): boolean {
    return this.scales.every((scale) => {
      const mapping = this.mappings[scale.id]
      // Before the initial load seeds them, `mappings` is empty — report
      // not-yet-valid rather than dereferencing an undefined mapping (this
      // getter is read via effect deps while status is still 'loading').
      return (
        mapping !== undefined && isScoreMappingDescending(mapping, scale.levels)
      )
    })
  }

  @computed
  get dirty(): boolean {
    // Changes vs the mount-time seed (the assignment's own config, for the
    // dialog). Drives the "unsaved changes" warning — NOT the save button.
    const mappingsDirty =
      JSON.stringify(this.mappings) !== JSON.stringify(this.seed)
    const gradeDirty =
      this.managesAssignFinalGrade &&
      this.assignFinalGrade !== this.savedAssignFinalGrade
    return mappingsDirty || gradeDirty
  }

  /**
   * Whether the current mappings (or the grade toggle) differ from what is
   * *currently saved* as the user's defaults — i.e. whether saving would change
   * anything. Distinct from `dirty` (vs the mount-time seed): an assignment
   * seeded from its own saved override still differs from a default that was
   * edited elsewhere after this panel mounted, and Save-as-Defaults must stay
   * enabled in that case.
   */
  @computed
  get differsFromSaved(): boolean {
    const mappingsDiffer = this.scales.some((scale) => {
      const current = this.mappings[scale.id]
      if (current === undefined) return false
      const saved =
        defaultMappingForScale(scale, this.savedDefaults) ??
        scale.defaultMapping
      return JSON.stringify(current) !== JSON.stringify(saved)
    })
    const gradeDiffers =
      this.managesAssignFinalGrade &&
      this.assignFinalGrade !== this.savedAssignFinalGrade
    return mappingsDiffer || gradeDiffers
  }

  /** Whether `save()` will do anything: loaded, valid, differs from what's
   * currently saved, and not in flight. */
  @computed
  get canSave(): boolean {
    return (
      this.status === 'ready' &&
      this.valid &&
      this.differsFromSaved &&
      !this.saving
    )
  }

  @action
  changeMapping(scaleId: ScaleId, next: Record<string, number>) {
    this.mappings = { ...this.mappings, [scaleId]: next }
  }

  @action
  setAssignFinalGrade(value: boolean) {
    this.assignFinalGrade = value
  }

  /**
   * The payload sent on save: the managed scales' current entries merged into
   * the saved defaults. The API replaces the whole array, so scales this cubit
   * doesn't manage are carried over from the saved set — otherwise saving from
   * an assignment that only has some scales would drop the others' mappings.
   */
  buildMergedDefaults(): ScoreMappingDefaultEntry[] {
    const current = buildScoreMappingDefaults([...this.scales], this.mappings)
    const managedTypes = new Set(current.map((entry) => entry.rubricType))
    return [
      ...this.savedDefaults.filter(
        (entry) => !managedTypes.has(entry.rubricType)
      ),
      ...current,
    ]
  }

  /**
   * Persists the current mappings (merged with the saved defaults). Returns
   * true on success; callers surface their own toast. No-ops unless `canSave`.
   */
  async save(): Promise<boolean> {
    if (!this.canSave) return false
    this.setSaving(true)
    const defaults = this.buildMergedDefaults()
    const body: {
      defaults: ScoreMappingDefaultEntry[]
      assignFinalGrade?: boolean
    } = this.managesAssignFinalGrade
      ? { defaults, assignFinalGrade: this.assignFinalGrade }
      : { defaults }
    const [, error] = await api.v1.users.scoreMappingDefaults
      .update({ body })
      .safe()
    this.settleSave(error ? null : defaults)
    return !error
  }

  @action
  private setSaving(value: boolean) {
    this.saving = value
  }

  @action
  private settleSave(saved: ScoreMappingDefaultEntry[] | null) {
    this.saving = false
    if (!saved) return
    // Move the dirty baseline forward to what we just persisted.
    this.savedDefaults = saved
    this.seed = this.mappings
    this.savedAssignFinalGrade = this.assignFinalGrade
  }
}
